import { useState, useRef } from "react";
import { theme } from "ui/theme";
import styled from "styled-components/macro";
import smoothScroll from "helpers/smoothScroll";
import Typography from "ui/Typography";
import TosCheckbox from "./TosCheckbox";
import OAuthButton from "../OAuthButton";
import Button from "ui/Button";
import { LeftChevron } from "ui/Icons";
import { useLocation, useNavigate } from "react-router-dom";

const $Wrapper = styled.div`
  display: flex;
  margin-top: 1rem;

  @media screen and (${theme.BreakpointLargeTabletDown}) {
    flex-direction: column;
    align-items: center;
    padding: 1rem;
    width: 100%;
  }
`;

interface StepWrapperProps {
  $active?: boolean;
}

const $StepWrapper = styled.div<StepWrapperProps>`
  width: 22.75rem;
  padding: 0.5rem 3rem 3rem 3rem;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media screen and (${theme.BreakpointLargeTabletDown}) {
    width: 100%;
    max-width: 31.25rem;
    &:first-child {
      margin-bottom: 1rem;
    }
  }

  @media not screen and (${theme.BreakpointLargeTabletDown}) {
    &:not(:last-child) {
      border: 1px solid ${theme.ColorSpace100};
      border-width: 0 1px 0 0;
    }
  }

  ${(props) => !props.$active && `opacity: 0.51;`}
`;

const $StepHeader = styled(Typography).attrs({
  size: "h3",
  forwardedAs: "span",
})`
  margin-bottom: 1.5rem;
`;

const $CheckboxWrapper = styled.div<StepWrapperProps>`
  background-color: ${theme.ColorWhite};
  border-radius: ${theme.RadiusMd};
  padding: 1.5rem;
`;

const $OAuthButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`;

const $BackButton = styled(Button)`
  margin-top: 0.5rem;
  border-radius: 0.5rem;
  align-self: flex-start;
  border: none;
  font-size: 0.75rem;
  min-height: 2rem;
  padding: 0 1rem 0 0.5rem;

  svg {
    height: 1rem;
  }
`;
export default function TeacherSignupSteps() {
  const [acceptedTos, setAcceptedTos] = useState(false);
  const refNextStep = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const isExpeditedOnboarding =
    params.get("intent") === "expedited_educator_onboarding" || false;

  const onChange = (checked: boolean) => {
    setAcceptedTos(checked);

    if (checked) {
      const breakpoint = 1024;
      if (window.innerWidth < breakpoint && refNextStep.current) {
        // auto-scroll to next step on mobile devices
        smoothScroll(refNextStep.current);
      }
    }
  };

  return (
    <div>
      <Typography size="h3">
        {isExpeditedOnboarding
          ? `Sign up for an educator account`
          : `Sign up with Along!`}
      </Typography>
      <$Wrapper>
        <$StepWrapper $active>
          <$StepHeader>Agree to our terms:</$StepHeader>
          <$CheckboxWrapper $active={acceptedTos}>
            <TosCheckbox checked={acceptedTos} onChange={onChange} />
          </$CheckboxWrapper>
          {!isExpeditedOnboarding && (
            <$BackButton
              analyticsName="clicked_teacher_signup_back_button"
              onClick={() => navigate(-1)}
              requestInProgress={false}
              variant="secondary"
            >
              <LeftChevron title="Previous page" role="img" />
              Back
            </$BackButton>
          )}
        </$StepWrapper>
        <$StepWrapper $active={acceptedTos} ref={refNextStep}>
          <$StepHeader>Sign up with your school email:</$StepHeader>
          <$OAuthButtonWrapper>
            <OAuthButton
              block
              disabled={!acceptedTos}
              type="Google"
              isSignup
              isExpeditedOnboarding={isExpeditedOnboarding}
            />
            <OAuthButton
              block
              disabled={!acceptedTos}
              type="Microsoft"
              isSignup
              isExpeditedOnboarding={isExpeditedOnboarding}
            />
          </$OAuthButtonWrapper>
        </$StepWrapper>
      </$Wrapper>
    </div>
  );
}
